<script lang="ts" setup>
import { NSelect, useMessage } from "naive-ui";
import { ref, watch, nextTick, computed, onMounted } from "vue";

const message = useMessage();

const props = withDefaults(
    defineProps<{
        modelValue: string | number | Array<string | number> | null | undefined;
        options: Array<{ label: string; value: string | number; disabled?: boolean }>;
        placeholder?: string;
        livePreview?: boolean;
        disabled?: boolean;
        loading?: boolean;
        status?: "success" | "error" | "warning" | "neutral";
        validateStatusOnTouch?: boolean; // if data is empty on touch (focus+blur), set status to error
        consistentMenuWidth?: boolean;
        multiple?: boolean;
        clearable?: boolean;
        showCopyBtn?: boolean;
        currentUserEmail?: string; // if provided, shows "Assign to me" button
        remote?: boolean;
        // open the menu and focus the input immediately when rendered
        autoOpen?: boolean;
        bgColor?: "success" | "error" | "warning" | "neutral";
    }>(),
    {
        placeholder: "",
        livePreview: false,
        disabled: false,
        loading: false,
        status: undefined,
        validateStatusOnTouch: false,
        consistentMenuWidth: false,
        multiple: false,
        clearable: false,
        showCopyBtn: false,
        currentUserEmail: undefined,
        remote: false,
        autoOpen: false,
        bgColor: undefined,
    },
);

const emits = defineEmits<{
    (
        _e: "update:modelValue",
        _value: string | number | Array<string | number> | null | undefined,
    ): void;
    (_e: "clear"): void;
    (_e: "search", _query: string): void;
    (_e: "update:show", _val: boolean): void;
}>();

const isEditing = ref(false);
const localValue = ref(props.modelValue);
const selectRef = ref<any>(null);
const showMenu = ref(false);
const localStatus = ref(props.status);
const touched = ref(false);

function handleFocus() {
    touched.value = true;
}

function handleBlur() {
    if (
        props.validateStatusOnTouch &&
        touched.value &&
        (localValue.value === null ||
            localValue.value === undefined ||
            localValue.value === "" ||
            (props.multiple && Array.isArray(localValue.value) && localValue.value.length === 0))
    ) {
        localStatus.value = "error";
    } else {
        localStatus.value = props.status;
    }
}

const computedStatus = computed(() => {
    const s = localStatus.value;
    return s === "neutral" ? undefined : s;
});

watch(
    () => props.modelValue,
    (val) => {
        localValue.value = val;
    },
);

function enterEditMode() {
    isEditing.value = true;
    touched.value = true;
    nextTick(() => {
        showMenu.value = true;
        selectRef.value?.focusInput?.();
    });
}

function exitEditMode() {
    isEditing.value = false;
    showMenu.value = false;
    if (localValue.value !== props.modelValue) {
        emits("update:modelValue", localValue.value);
    }
}

function handleKeydown(e: KeyboardEvent) {
    if (e.key === "Enter") {
        exitEditMode();
    }
}

function handleUpdateShow(val: boolean) {
    showMenu.value = val;
    emits("update:show", val);
    if (!val) {
        exitEditMode();
    }
}

function handleUpdateValue(val: string | number | Array<string | number> | null | undefined) {
    localValue.value = val;
    if (val !== props.modelValue) {
        emits("update:modelValue", val);
    }
}

function handleClear() {
    const val = props.multiple ? [] : null;
    localValue.value = val;
    emits("update:modelValue", val);
    emits("clear");
}

function handleAssignToMe() {
    if (props.currentUserEmail) {
        localValue.value = props.currentUserEmail;
        emits("update:modelValue", props.currentUserEmail);
    }
}

function handleSearch(query: string) {
    emits("search", query);
}

// Open menu and focus when autoOpen is true
onMounted(() => {
    if (props.autoOpen) {
        nextTick(() => {
            showMenu.value = true;
            // Ensure the underlying NSelect input receives focus
            selectRef.value?.focusInput?.();
        });
    }
});

watch(
    () => props.autoOpen,
    (val) => {
        if (val) {
            nextTick(() => {
                showMenu.value = true;
                selectRef.value?.focusInput?.();
            });
        }
    },
);

const bgClass = computed(() => {
    switch (props.bgColor) {
        case "success":
            return "bg-[#99E6CC] rounded-[2em]";
        case "error":
            return "bg-[#FECECD] rounded-[2em]";
        case "warning":
            return "bg-[#FBDCB1] rounded-[2em]";
        case "neutral":
            return "bg-slate-200 rounded-[2em]";
        default:
            return "";
    }
});

const selectThemeOverrides = computed(() => {
    if (!props.bgColor) return undefined;
    return {
        peers: {
            InternalSelection: {
                color: "transparent",
                colorActive: "transparent",
                colorDisabled: "transparent",
                border: "transparent",
            },
        },
    } as any;
});

const formattedValue = computed(() => {
    return props.multiple
        ? Array.isArray(localValue.value) && localValue.value.length > 0
            ? props.options
                  .filter(
                      (opt) =>
                          Array.isArray(localValue.value) && localValue.value?.includes(opt.value),
                  )
                  .map((opt) => opt.label)
                  .join(", ")
            : props.placeholder || "—"
        : props.options.find((opt) => opt.value === localValue.value)?.label ||
              props.placeholder ||
              "—";
});

function handleCopyLabel() {
    const label = formattedValue.value;
    if (label && label !== "—") {
        navigator.clipboard.writeText(label);
        message.info("Copied!", {
            duration: 1000,
        });
    }
}

const isCopyValid = computed(() => !!(formattedValue.value && formattedValue.value !== "—"));

function setInternalValue(val: string | number | Array<string | number> | null | undefined) {
    localValue.value = val;
}

defineExpose({
    localValue,
    setInternalValue,
});
</script>

<template>
    <div>
        <div class="relative w-full">
            <n-select
                v-if="!livePreview || isEditing"
                ref="selectRef"
                v-model:value="localValue"
                :options="options"
                :placeholder="placeholder"
                :show="showMenu"
                filterable
                :loading="loading"
                :disabled="disabled"
                :status="computedStatus"
                :consistent-menu-width="consistentMenuWidth"
                :multiple="multiple"
                :clearable="clearable"
                :remote="remote"
                :class="['w-full', bgClass]"
                :theme-overrides="selectThemeOverrides"
                @clear="handleClear"
                @focus="handleFocus"
                @blur="handleBlur"
                @search="handleSearch"
                @update:show="handleUpdateShow"
                @update:value="handleUpdateValue"
                @keydown="handleKeydown"
            />
            <p
                v-else
                :class="[
                    'cursor-pointer capitalize flex items-center px-3 min-h-[34px] rounded-[2em] w-full border border-transparent hover:bg-cream transition-colors duration-150',
                    bgClass,
                ]"
                @click="enterEditMode"
            >
                {{ formattedValue }}
            </p>
            <button
                v-if="showCopyBtn"
                :class="[
                    'material-symbols-outlined text-gray-300 text-lg absolute top-0.5 right-7 z-10',
                    !isCopyValid ? 'cursor-not-allowed opacity-50' : 'active:scale-95',
                ]"
                :disabled="!isCopyValid"
                @click="handleCopyLabel"
            >
                content_copy
            </button>
        </div>
        <button
            v-if="currentUserEmail"
            class="flex font-medium text-sm hover:underline w-fit active:scale-95 active:filter active:brightness-90 ml-2"
            type="button"
            @click="handleAssignToMe"
        >
            Assign to me
        </button>
    </div>
</template>
